import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import LanguageMenuWrapper from '../components/LanguageMenuWrapper';
import ReCaptcha, { ReCaptchaRef } from '../components/ReCaptcha';
import { useSupportPhoneDisplay } from '../utils';
import { getAppConfig } from '../utils/config';
import { AXON_LOGO_PATH, getSupportMessage, SUPPORT_PHONE_TEL } from '../utils/constants';
import {
  checkForValidSession,
  createSession
} from '../utils/sessionUtils';

const CreateSessionPage: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCaptchaRef>(null);
  const supportPhoneDisplay = useSupportPhoneDisplay();
  const history = useHistory();
  
  // Get validated configuration
  const config = getAppConfig();

  /**
   * Check for valid session and redirect accordingly
   */
  useEffect(() => {
    if (checkForValidSession()) {
      history.push('/guide');
    }
  }, [history]);

  const handleRecaptchaVerify = (token: string) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      setError(__('Please complete the reCAPTCHA verification'));
      return;
    }

    setLoading(true);
    setError('');

    await createSession(
      recaptchaToken,
      // onSuccess
      () => {
        history.push('/guide');
      },
      // onError
      (errorMessage: string) => {
        setError(errorMessage);
        // Reset reCAPTCHA on error
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
        setLoading(false);
      }
    );
  };

  const handleLookupRedirect = () => {
    window.location.href = '/';
  };

  // Show error with link to lookup page if no valid serial number
  const errorContent = error && error.includes('We couldn\'t recognize your device') ? (
    <Typography 
      variant="body2" 
      sx={{ 
        mt: 2, 
        textAlign: 'center',
        color: 'error.main'
      }}
    >
      {__('We couldn\'t recognize your device. Please try scanning the QR code again. Don\'t have the QR code? $[link]', {
        link: (
          <MuiLink
            component="button"
            variant="body2"
            onClick={handleLookupRedirect}
            sx={{ color: 'primary.main', textDecoration: 'underline' }}
            key="lookup-link"
          >
            {__('Click here to continue without it.')}
          </MuiLink>
        )
      })}
    </Typography>
  ) : error ? (
    <Typography 
      variant="body2" 
      sx={{ 
        mt: 2, 
        textAlign: 'center',
        color: 'error.main'
      }}
    >
      {error}
    </Typography>
  ) : null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // Mobile-first responsive design
        padding: { xs: 2, sm: 3, md: 4 },
        '@media (max-width: 768px)': {
          padding: 1,
          minHeight: '100vh'
        }
      }}
    >
        {/* Mobile-optimized container */}
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', sm: 420, md: 480 },
            margin: '0 auto',
            bgcolor: 'white',
            borderRadius: { xs: 0, sm: 2 },
            boxShadow: { xs: 'none', sm: '0 4px 6px rgba(0, 0, 0, 0.1)' },
            overflow: 'hidden',
            minHeight: { xs: 'auto', sm: 'auto' },
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header Section with Logo */}
          <Box
            sx={{
              bgcolor: 'white',
              p: { xs: 2, sm: 3 },
              pb: { xs: 1.5, sm: 2 },
              display: 'flex',
              justifyContent: 'center',
              borderBottom: { xs: 'none', sm: '1px solid #e0e0e0' }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '175.89px',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: '7px'
              }}
            >
              <Box
                component="img"
                src={AXON_LOGO_PATH}
                alt="Axon Logo"
                sx={{
                  height: { xs: 28, sm: 32 },
                  width: 'auto'
                }}
              />
            </Box>
          </Box>

          {/* Mobile-optimized line separator */}
          <Divider 
            sx={{ 
              borderColor: '#e0e0e0',
              borderWidth: 1,
              display: { xs: 'block', sm: 'none' }
            }} 
          />

          {/* Main Content Section */}
          <Box
            sx={{
              bgcolor: '#fafafa',
              p: { xs: 2, sm: 3 },
              flex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Welcome Text - Mobile optimized */}
            <Typography 
              variant="h3"
              component="h1" 
              sx={{ 
                mb: { xs: 1.5, sm: 2 },
                textAlign: 'center'
              }}
            >
              {__('Before we get started.')}
            </Typography>
            
            <Typography 
              variant="h6"
              sx={{ 
                mb: { xs: 2, sm: 3 },
                textAlign: 'left'
              }}
            >
              {__('Let\'s make sure you\'re not a robot.')}
            </Typography>

            {/* Form - Mobile optimized */}
            <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1 }}>
              {/* reCAPTCHA - Mobile optimized */}
              <Box sx={{ 
                mb: { xs: 2, sm: 2 }, 
                display: 'flex', 
                justifyContent: 'center',
                '& > div': {
                  transform: { xs: 'scale(0.9)', sm: 'scale(1)' },
                  transformOrigin: 'center'
                }
              }}>
                <ReCaptcha
                  ref={recaptchaRef}
                  siteKey={config.googleRecaptchaSiteKey}
                  onVerify={handleRecaptchaVerify}
                  size="normal"
                  theme="light"
                  disabled={loading}
                />
              </Box>

              {/* Submit Button - Mobile optimized */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !recaptchaToken}
                aria-label={__('Continue')}
                sx={{
                  py: { xs: 1.5, sm: 1.25 },
                  borderRadius: 1,
                  mb: { xs: 2, sm: 3 },
                  minHeight: { xs: '48px', sm: '40px' }, // Touch-friendly height
                  '&:disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled'
                  }
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : __('Continue')}
              </Button>

              {/* Error Display */}
              {errorContent}

              {/* Support Info - Mobile optimized */}
              <Typography 
                variant="caption" 
                sx={{ 
                  textAlign: 'center',
                  mt: 'auto',
                  pt: { xs: 1, sm: 0 },
                  display: 'block'
                }}
              >
                {getSupportMessage()}{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 500,
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(`tel:${SUPPORT_PHONE_TEL}`)}
                >
                  {supportPhoneDisplay}
                </Box>
                .
              </Typography>
            </Box>
          </Box>
        </Box>
      {/* Language Menu */}
      {config.enableI18n && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 3,
            pb: 2,
          }}
        >
          <LanguageMenuWrapper />
        </Box>
      )}
    </Box>
  );
};

export default CreateSessionPage; 